import React, { useMemo, useEffect, useState } from 'react';
import {
  CellTypes,
  DataGrid,
  GridColDef,
  Checkbox,
  Stack,
  Icon,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import { PurchaseOrder } from '../../Lib/Types/purchase-order';

import { cleanPurchaseOrderLine } from './Utils/cleanPurchaseOrderLine';
import { StringCell, TabCell } from './PurchaseOrderDetailDataGridCells';

type Row = {
  id: string;
  selected: CellTypes.DataGridCellCustomParams;
  item: CellTypes.DataGridCellCustomParams;
  fn_upc: CellTypes.DataGridCellCustomParams;
  prop65: CellTypes.DataGridCellCustomParams;
};

interface Prop65DataGridProps {
  loading?: boolean;
  purchaseOrder: PurchaseOrder;
  selectedRows: Record<string, boolean>;
  prop65Values: Record<string, 'yes' | 'no' | null>;
  onSelectionChange: (rows: Record<string, boolean>) => void;
  onUpdateProp65: (id: number, value: 'yes' | 'no') => void;
  showErrors?: boolean;
}

export function Prop65DataGrid({
  loading,
  purchaseOrder,
  selectedRows,
  prop65Values,
  onSelectionChange,
  onUpdateProp65,
  showErrors = false,
}: Prop65DataGridProps) {
  const { purchase_order_lines: rows } = purchaseOrder;

  const [internalShowErrors, setInternalShowErrors] = useState(showErrors);

  useEffect(() => {
    setInternalShowErrors(showErrors);
  }, [showErrors]);

  const effectiveShowErrors = internalShowErrors;

  const hasAnySelection = Object.values(selectedRows).some(Boolean);

  const areAllRowsSelected =
    rows.length > 0 &&
    Object.keys(selectedRows).length === rows.length &&
    Object.values(selectedRows).every(Boolean);

  const transformedRows = useMemo(
    () =>
      rows.map((lineItem) => {
        const cleanLine = cleanPurchaseOrderLine(lineItem);
        const id = lineItem.po_line_id;

        const currentValue =
          prop65Values[id] !== undefined ? prop65Values[id] : null;

        const hasError = effectiveShowErrors && currentValue === null;

        return {
          id: id.toString(),
          selected: (
            <Checkbox
              checked={!!selectedRows[id]}
              onChange={() =>
                onSelectionChange({
                  ...selectedRows,
                  [id]: !selectedRows[id],
                })
              }
              disabled={loading}
            />
          ),
          item: (
            <div className="truncate">
              <StringCell data={cleanLine.item_name} variant="primary" />
            </div>
          ),
          fn_upc: (
            <div className="truncate text-base-secondary">
              <StringCell data={cleanLine.upc} variant="secondary" />
            </div>
          ),
          prop65: (
            <TabCell
              key={`${id}-${hasError ? 'error' : 'normal'}`}
              value={currentValue}
              onChange={(value) => {
                onUpdateProp65(id, value);
              }}
              error={hasError}
            />
          ),
        } satisfies Row;
      }),
    [
      rows,
      loading,
      selectedRows,
      prop65Values,
      onSelectionChange,
      onUpdateProp65,
      effectiveShowErrors,
    ],
  );

  const columns: GridColDef[] = [
    {
      field: 'selected',
      renderHeader: () => (
        <Checkbox
          checked={areAllRowsSelected}
          onChange={() => {
            if (hasAnySelection) {
              onSelectionChange({});
            } else {
              const newSelectedRows = rows.reduce(
                (acc, line) => {
                  acc[line.po_line_id] = true;
                  return acc;
                },
                {} as Record<string, boolean>,
              );
              onSelectionChange(newSelectedRows);
            }
          }}
          disabled={loading}
        />
      ),
      render: 'custom',
      display: 'flex',
      flex: 0.3,
      sortable: false,
      minWidth: 50,
      disableColumnMenu: true,
    },
    {
      field: 'prop65',
      renderHeader: () => (
        <Stack
          direction="row"
          align="center"
          gap="sm"
          className="text-[#1D24338C] cursor-pointer text-xs"
          onClick={() => {
            window.open(
              'https://www.p65warnings.ca.gov/sites/default/files/art_6_business_qa.pdf',
              '_blank',
            );
          }}
        >
          <span>CA Prop 65</span>
          <Icon variant="solid" name="information/information-circle" />
        </Stack>
      ),
      render: 'custom',
      display: 'flex',
      flex: 1.5,
      minWidth: 190,
      disableColumnMenu: true,
    },
    {
      field: 'item',
      headerName: 'Item',
      render: 'custom',
      display: 'flex',
      flex: 1.5,
      sortable: false,
      minWidth: 200,
      disableColumnMenu: true,
    },
    {
      field: 'fn_upc',
      headerName: 'FN UPC',
      render: 'custom',
      display: 'flex',
      flex: 1.5,
      sortable: true,
      minWidth: 250,
      disableColumnMenu: true,
    },
  ];

  return (
    <div className="flex flex-col gap-4 overflow-x-auto">
      <DataGrid
        rows={transformedRows}
        columns={columns}
        hideFooter
        autoHeight
        loading={loading}
      />
      {effectiveShowErrors && (
        <div className="flex items-center">
          <Icon
            name="alert/alert-circle"
            className="nu-text-danger-700"
            size="16px"
          />
          <P color="error" size="xsm" className="ml-2" weight="semibold">
            You must indicate whether each SKU requires CA Prop 65 by selecting
            &apos;Yes&apos; or &apos;No&apos;.{' '}
          </P>
        </div>
      )}
    </div>
  );
}
